import { Card, CardContent } from "@/components/ui/card";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { useIngredientCategoriesLookupQuery } from "@/modules/inventory/presentation/api/ingredient-categories-queries";
import {
  TRANSFER_FILTER_BODY,
  TRANSFER_FILTER_CARD,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect } from "react";

const ALL = "__all__";
const SELECT_CLASS = "h-9 w-full min-w-0 sm:min-w-[12rem] sm:w-auto";

export type LowStockReportsSearch = {
  branchId?: string;
  itemType?: string;
  categoryId?: string;
  offset?: number;
  limit?: number;
};

export function LowStockReportsFilter() {
  const nav = useNavigate({ from: "/app/reports/inventory/low-stock" });
  const search = useSearch({
    from: "/app/reports/inventory/low-stock",
  }) as LowStockReportsSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });
  const branches: BranchRow[] = branchesData?.data ?? [];
  const hub = branches.find((b) => b.isDistributionHub);
  const categoriesLookup = useIngredientCategoriesLookupQuery();

  useEffect(() => {
    if (search.branchId || !hub?.id) return;
    nav({
      search: { ...search, branchId: hub.id },
      replace: true,
    });
  }, [hub?.id, search.branchId, nav]);

  const branchOptions = branches.map((b) => ({
    value: b.id,
    label: b.isDistributionHub ? `${b.name} (Hub)` : `${b.name} (${b.code})`,
  }));

  const itemTypeOptions = [
    { value: ALL, label: "ທຸກປະເພດ" },
    { value: "INGREDIENT", label: "ວັດຖຸດິບ" },
    { value: "RETAIL_VARIANT", label: "ຕົວແບບສິນຄ້າ" },
  ];

  const categoryOptions = [
    { value: ALL, label: "ທຸກຫມວດ" },
    { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
    ...(categoriesLookup.data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
    })),
  ];

  const hasFilter =
    (search.itemType && search.itemType !== ALL) ||
    (search.categoryId && search.categoryId !== ALL);

  const update = (next: Partial<LowStockReportsSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  return (
    <Card className={TRANSFER_FILTER_CARD}>
      <CardContent className={`${TRANSFER_FILTER_BODY} w-full flex-wrap gap-3 py-3`}>
        <SimpleSelect
          className={SELECT_CLASS}
          placeholder="ສາຂາ"
          value={search.branchId ?? hub?.id ?? ""}
          onValueChange={(v) => update({ branchId: v })}
          options={branchOptions}
        />
        <SimpleSelect
          className={SELECT_CLASS}
          value={search.itemType ?? ALL}
          onValueChange={(v) =>
            update({
              itemType: v === ALL ? undefined : v,
              categoryId:
                v !== "INGREDIENT" ? undefined : search.categoryId,
            })
          }
          options={itemTypeOptions}
        />
        <SimpleSelect
          className={SELECT_CLASS}
          value={search.categoryId ?? ALL}
          disabled={(search.itemType ?? ALL) === "RETAIL_VARIANT"}
          onValueChange={(v) =>
            update({ categoryId: v === ALL ? undefined : v })
          }
          options={categoryOptions}
        />
        {hasFilter ? (
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
            onClick={() =>
              nav({
                search: {
                  branchId: search.branchId ?? hub?.id,
                  offset: 0,
                  limit: search.limit,
                },
              })
            }
          >
            <XIcon className="size-4" />
            ລ້າງຕົວກອງ
          </button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function lowStockReportParams(
  search: LowStockReportsSearch,
  hubBranchId?: string,
) {
  const branchId = search.branchId ?? hubBranchId;
  if (!branchId) return null;
  return {
    branchId,
    itemType:
      search.itemType && search.itemType !== ALL
        ? (search.itemType as "INGREDIENT" | "RETAIL_VARIANT")
        : undefined,
    categoryId:
      search.categoryId && search.categoryId !== ALL
        ? search.categoryId
        : undefined,
    limit: search.limit ?? 100,
    offset: search.offset ?? 0,
  };
}
